<template>
	<div
		class="group relative bg-white border border-blue-100 rounded-xl shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer overflow-hidden w-full hover:-translate-y-1"
	>
		<!-- Status Indicator Bar -->
		<div
			:class="[
				'h-2 w-full',
				account.accountStatus === 'ACTIVE'
					? 'bg-green-500'
					: 'bg-red-500',
			]"
		></div>

		<!-- Main Content -->
		<div class="p-4 sm:p-5 space-y-4">
			<!-- Header: Date and Status -->
			<div class="flex items-start justify-between gap-3">
				<div class="min-w-0 flex-1">
					<p class="font-semibold text-gray-900 text-sm sm:text-base">
						{{ formatAccountType(account.accountType) }}
					</p>
					<p class="text-xs sm:text-sm text-gray-600 mt-1">
						Account • {{ account.accountCurrency }}
					</p>
				</div>

				<!-- Status Badge -->
				<div class="flex-shrink-0">
					<span
						:class="[
							'inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border',
							account.accountStatus === 'ACTIVE'
								? 'bg-green-50 text-green-700 border-green-200'
								: 'bg-red-50 text-red-700 border-red-200',
						]"
					>
						<div
							:class="[
								'w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0',
								account.accountStatus === 'ACTIVE'
									? 'bg-green-500'
									: 'bg-red-500',
							]"
						></div>
						<span class="hidden xs:inline">{{
							account.accountStatus
						}}</span>
						<span class="xs:hidden">{{
							account.accountStatus === 'ACTIVE'
								? 'Active'
								: 'Inactive'
						}}</span>
					</span>
				</div>
			</div>

			<!-- Balance Section -->
			<div
				class="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-100"
			>
				<div class="flex items-center justify-between">
					<span class="text-xs sm:text-sm font-medium text-blue-700"
						>Available Balance</span
					>
					<div class="flex items-baseline gap-1 sm:gap-2">
						<span
							class="text-xs sm:text-sm font-medium text-blue-600"
						>
							{{ getCurrencySymbol(account.accountCurrency) }}
						</span>
						<span
							class="text-lg sm:text-xl font-bold text-blue-900"
						>
							{{
								formatBalance(
									account.availableBalance || account.balance,
								)
							}}
						</span>
					</div>
				</div>

				<!-- Account Number -->
				<div
					class="flex items-center justify-between mt-2 pt-2 border-t border-blue-200/50"
				>
					<span class="text-xs text-blue-600">Account Number</span>
					<span class="text-xs font-medium text-blue-800 font-mono">
						{{ truncateAccountNumber(account.accountNumber) }}
					</span>
				</div>
			</div>

			<!-- Reference Section -->
			<div
				class="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2"
				v-if="account.accountNumber"
			>
				<span class="text-xs font-medium text-gray-500 flex-shrink-0"
					>Full Account No.</span
				>
				<span
					class="text-xs text-gray-700 font-mono bg-gray-50 px-2 sm:px-3 py-1.5 rounded-lg border border-gray-200 break-all min-w-0 text-right"
				>
					{{ account.accountNumber }}
				</span>
			</div>

			<!-- Action Hover Overlay -->
			<div
				class="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none"
			></div>

			<!-- View Transactions Hint -->
			<div
				@click="viewTransaction"
				class="flex items-center justify-center pt-3 border-t border-gray-100"
			>
				<span
					class="text-xs text-gray-500 group-hover:text-blue-600 transition-colors duration-300 flex items-center gap-1.5"
				>
					<svg
						class="w-3 h-3 sm:w-3.5 sm:h-3.5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
						/>
					</svg>
					<span class="hidden sm:inline"
						>Click to view transactions</span
					>
					<span class="sm:hidden">View transactions</span>
				</span>
			</div>
		</div>

		<!-- Focus Ring for Accessibility -->
		<div
			class="absolute inset-0 rounded-xl ring-2 ring-blue-500 ring-offset-1 opacity-0 group-focus-within:opacity-100 transition-opacity duration-200 pointer-events-none"
		></div>
	</div>
</template>

<script>
export default {
	name: 'AccountItem',
	props: {
		account: {
			type: Object,
			required: true,
			default: () => ({
				accountNumber: '',
				accountType: '',
				accountStatus: 'ACTIVE',
				accountCurrency: 'KES',
				availableBalance: 0,
				balance: 0,
				lastUpdated: null,
			}),
		},
	},
	methods: {
		formatAccountType(type) {
			if (!type) return 'Wallet Account';
			return type
				.split('_')
				.map(
					(word) =>
						word.charAt(0).toUpperCase() +
						word.slice(1).toLowerCase(),
				)
				.join(' ');
		},
		viewTransaction() {
			this.$store.commit('showViewAccount', true);
		},

		formatBalance(balance) {
			const amount = parseFloat(balance) || 0;
			return amount.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},

		getCurrencySymbol(currency) {
			const symbols = {
				USD: '$',
				EUR: '€',
				GBP: '£',
				KES: 'KSh',
			};
			return symbols[currency] || currency;
		},

		truncateAccountNumber(number) {
			if (!number) return '';
			if (number.length <= 8) return number;
			return (
				number.substring(0, 4) +
				'...' +
				number.substring(number.length - 4)
			);
		},
	},
};
</script>

<style scoped>
/* Focus styles for accessibility */
.group:focus {
	outline: none;
}

/* Enhanced hover effects */
.group:hover {
	transform: translateY(-2px);
	box-shadow:
		0 10px 25px -5px rgba(59, 130, 246, 0.1),
		0 4px 6px -2px rgba(59, 130, 246, 0.05);
}

/* High contrast mode support */
@media (prefers-contrast: high) {
	.border-blue-100,
	.border-gray-100,
	.border-gray-200 {
		border-color: #000;
	}

	.text-gray-900 {
		color: #000;
	}

	.text-gray-600,
	.text-gray-500,
	.text-gray-700 {
		color: #333;
	}

	.bg-blue-50,
	.bg-gray-50 {
		background: #f8f9fa !important;
	}
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.transition-opacity,
	.group:hover {
		transition: none !important;
		transform: none !important;
	}
}

/* Print styles */
@media print {
	.shadow-sm,
	.hover\:shadow-lg {
		box-shadow: none !important;
	}

	.bg-blue-50,
	.bg-gray-50 {
		background: #f8f9fa !important;
	}

	.group:hover {
		transform: none !important;
	}
}

/* Enhanced focus ring */
.group:focus-within .ring-2 {
	box-shadow:
		0 0 0 1px #ffffff,
		0 0 0 3px #3b82f6;
}

/* Custom breakpoint for extra small screens */
@media (min-width: 475px) {
	.xs\:inline {
		display: inline !important;
	}

	.xs\:hidden {
		display: none !important;
	}

	.xs\:flex-row {
		flex-direction: row !important;
	}

	.xs\:items-center {
		align-items: center !important;
	}

	.xs\:justify-between {
		justify-content: space-between !important;
	}
}

/* Mobile optimizations */
@media (max-width: 640px) {
	.text-base {
		font-size: 0.875rem;
	}

	.text-lg {
		font-size: 1.125rem;
	}

	.p-5 {
		padding: 1rem;
	}
}

/* Small mobile optimizations */
@media (max-width: 375px) {
	.p-4 {
		padding: 0.75rem;
	}

	.space-y-4 > * + * {
		margin-top: 0.75rem;
	}
}
</style>
